import { Fragment, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useAppDisplayModeContext } from '@archipro-design/aria';

import { RoccoLink } from '@rocco/components/link';
import { useProfessionalInitialRouteData } from '@modules/professional/hook/use-professional-initial-route-data';
import { useEnquiryModal } from '~/modules/enquiry/hooks/use-enquiry-modal';
import { useEnquiryVisibility } from '~/modules/enquiry/hooks/use-enquiry-visibility';
import {
    useBookMeetingGateEnabled,
    useOpenBookMeetingGate,
} from '~/modules-rocco/booking/book-meeting-gate-state';
import { PrimaryCtaEnum } from 'generated/graphql';
import {
    DetailSaveButton,
    detailCta,
    useIslandSection,
    type IslandMode,
} from '~/ui-rocco/pages/detail-shared';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';
import { buildProfessionalTrustSignals } from '../utils/professional-trust-signals';
import { ProfessionalTrustBadges } from './ProfessionalTrustBadges';
import { ProfileFamilyLine } from './ProfileFamilyLine';

/**
 * The professional's identity block, sitting on white below
 * {@link import('./ProfessionalProfileHero')}: the name, a discipline · location
 * line, a row of validation stats (projects/products count, reply rate, typical
 * project value) and the key CTA cluster — Get in touch, Visit Website, Save.
 *
 * Modelled on modern marketplace profiles (the name/stats/CTA move off the
 * banner onto white). The primary enquiry action is prominent here at the top;
 * while it's on screen the header registers a `hidden` island claim so the
 * persistent pill only reappears once the CTA scrolls away.
 */
export const ProfessionalProfileHeader = () => {
    const professional = useProfessionalInitialRouteData();
    const { pathname } = useLocation();
    const { mobile } = useAppDisplayModeContext();
    const { showEnquiryCTA } = useEnquiryVisibility(professional);
    const { openEnquiryModal } = useEnquiryModal({
        professional,
        itemType: 'professional',
        siteTreeID: professional.ID,
        itemTitle: professional.Title,
        includeBranch: true,
        source: 'ProfessionalHeader',
    });
    const enquiryLabel =
        professional.primaryCTA?.primaryCTALabelTitleCase ?? 'Get in touch';

    /*
     * A CTA that says "Book a Consultation" opens the booking flow rather than
     * the generic enquiry form. `AutoOptimise` counts because it resolves to
     * that label — it is the column default, so it is what most profiles show.
     *
     * `useBookMeetingGateEnabled` is `book_meeting_open_access` and
     * `revamp_2026` together, so until the former is turned on this still lands
     * on the enquiry modal, titled as the booking request it is. That is the
     * same fallback the meta row's Book a Meeting already uses.
     */
    const ctaEnum = professional.primaryCTA?.primaryCTA;
    const ctaIsBooking =
        ctaEnum === PrimaryCtaEnum.BookConsultation ||
        ctaEnum === PrimaryCtaEnum.AutoOptimise;
    const bookMeetingEnabled = useBookMeetingGateEnabled();
    const openBookMeetingGate = useOpenBookMeetingGate();

    const onPrimaryCta = () => {
        if (ctaIsBooking && bookMeetingEnabled) {
            openBookMeetingGate({
                professionalUrlSegment: professional.URLSegment,
                siteTreeId: professional.ID,
                professionalName: professional.Title,
            });
            return;
        }
        openEnquiryModal({ title: enquiryLabel, ctaLabel: enquiryLabel });
    };

    // While the header is the section crossing the reading line, claim the
    // island as `hidden` so the persistent enquiry pill doesn't double up with
    // the CTA right here — the pill takes over once the header scrolls past.
    const hiddenMode = useMemo<IslandMode>(() => ({ kind: 'hidden' }), []);
    const islandRef = useIslandSection<HTMLElement>(hiddenMode, {
        enabled: mobile && showEnquiryCTA,
        // Beat any in-page section that also wants the island at the top.
        priority: 10,
    });

    const discipline = professional.PrimaryCategory?.PrettyTitle;
    const location = professional.LibraryLocations[0];
    const locationLabel = location?.City || location?.Region || location?.Title;
    const subtitle = [discipline, locationLabel].filter(Boolean).join(' · ');

    // Type-aware trust signals (verification/awards badges + validation stats),
    // ordered strongest-first for a hiring homeowner / specifying PM.
    const { badges, stats, typicalJob, associations } =
        buildProfessionalTrustSignals(
            professional,
            professionalTabBase(professional.URLSegment, pathname),
        );

    return (
        <section
            ref={islandRef}
            className="bg-ground max-md:px-6 max-md:pt-12 md:px-[3.125rem] md:pt-[4.5rem]"
        >
            <h1 className="m-0 font-sans font-normal text-[#141414] max-md:text-[2rem] max-md:leading-[1.08] max-md:tracking-[-0.02em] md:text-[3.25rem] md:leading-[1.04] md:tracking-[-0.06em]">
                {professional.Title}
            </h1>
            {subtitle ? (
                <p className="m-0 mt-2 font-sans text-[#6a6a6a] max-md:text-sm md:mt-3 md:text-[1.0625rem]">
                    {subtitle}
                </p>
            ) : null}
            {stats.length > 0 ? (
                <div className="mt-4 flex flex-wrap items-baseline gap-x-2 gap-y-1 border-t border-black/10 pt-4 font-sans text-base md:mt-5 md:pt-5">
                    {stats.map((stat, index) => {
                        const value = (
                            <span className="font-medium text-[#141414]">
                                {stat.value}
                            </span>
                        );
                        const label = (
                            <span className="text-[#8a8a8a]">{stat.label}</span>
                        );
                        const inner = stat.labelFirst ? (
                            <>
                                {label} {value}
                            </>
                        ) : (
                            <>
                                {value} {label}
                            </>
                        );
                        const el = stat.link ? (
                            <RoccoLink
                                to={stat.link}
                                prefetch="intent"
                                preventScrollReset
                                className="transition-opacity hover:opacity-60"
                            >
                                {inner}
                            </RoccoLink>
                        ) : (
                            <span>{inner}</span>
                        );
                        return (
                            <Fragment key={stat.id}>
                                {index > 0 ? (
                                    <span
                                        aria-hidden="true"
                                        className="text-[#c4c4c4]"
                                    >
                                        &middot;
                                    </span>
                                ) : null}
                                {el}
                            </Fragment>
                        );
                    })}
                </div>
            ) : null}

            <ProfessionalTrustBadges
                badges={badges}
                typicalJob={typicalJob}
                associations={associations}
            />

            {/* Mobile has no sub-nav pill row to hang the family line off, so it
                reads as another backing signal under the credentials — the logo
                stack leading the copy, since the mobile banner carries no marks
                of its own. */}
            <ProfileFamilyLine withMarks className="mt-3 md:hidden" />

            {/* Key CTA at the top: full-width primary over a Visit / Save row on
                mobile; an inline row on desktop. */}
            <div className="mt-6 flex gap-2 max-md:flex-col max-md:gap-3 md:mt-8 md:items-center">
                {showEnquiryCTA ? (
                    <button
                        type="button"
                        onClick={onPrimaryCta}
                        className={detailCta(
                            'filled-light',
                            'max-md:h-[3.125rem] max-md:w-full max-md:text-sm md:min-w-[16rem]',
                        )}
                    >
                        {enquiryLabel}
                        <svg
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                            className="size-[0.83em]"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                        >
                            <path d="M5 12h13M12 5l7 7-7 7" />
                        </svg>
                    </button>
                ) : null}
                <div className="flex items-stretch gap-2 max-md:w-full">
                    {professional.Website ? (
                        <RoccoLink
                            to={professional.Website}
                            newTab
                            className={detailCta(
                                'outlined-light',
                                'max-md:h-[3.125rem] max-md:flex-1 max-md:text-sm md:w-[12.1875rem]',
                            )}
                        >
                            Visit Website
                        </RoccoLink>
                    ) : null}
                    <DetailSaveButton
                        pinId={professional.ID}
                        pinType="ProfessionalPin"
                        theme="light"
                        className="max-md:h-[3.125rem] max-md:w-[3.125rem] max-md:shrink-0"
                    />
                </div>
            </div>
        </section>
    );
};
